import {t} from './localeText';
import {useLayoutEffect, useRef, useState} from 'react';
import ProjectEvidence, {emphasizeEvidence, projectEvidence} from './ProjectEvidence';
import ProjectScene, {projectScenes} from './ProjectScene';
import ProjectArchitecture from './ProjectArchitecture';
import s from './index.module.less';

const SummaryDetailDialog = ({item, lang, onDismiss}) => {
    const dialogRef = useRef(null);
    const titleRef = useRef(null);
    const bodyRef = useRef(null);
    const pointerFrame = useRef(null);
    const [motionPaused, setMotionPaused] = useState(false);
    const card = item.payload;
    const isProject = item.type === 'project';
    const emphasis = isProject ? (card.evidence || projectEvidence[card.title])?.emphasis : undefined;
    const scene = isProject ? projectScenes[card.title] || (card.id ? {code: card.id.toUpperCase(), flow: [card.category, card.stage]} : undefined) : undefined;
    const labels = lang === 'zh'
        ? {project: '项目详情', domain: '能力域', close: '关闭详情', overview: '项目概述', result: '验证与成果', details: '项目说明与技术细节'}
        : lang === 'ja'
            ? {project: 'プロジェクト詳細', domain: '専門領域', close: '詳細を閉じる', overview: '概要', result: '検証と成果', details: '詳細'}
            : {project: 'Project details', domain: 'Capability', close: 'Close details', overview: 'Overview', result: 'Evidence & outcomes', details: 'Project & technical details'};

    useLayoutEffect(() => {
        const dialog = dialogRef.current;
        const trigger = document.activeElement;
        const scrollRoot = document.getElementById('ushouldknow-scroll-root');
        const previousOverflow = scrollRoot?.style.overflow;
        if (scrollRoot) scrollRoot.style.overflow = 'hidden';
        dialog.showModal();
        titleRef.current?.focus({preventScroll: true});
        return () => {
            cancelAnimationFrame(pointerFrame.current);
            dialog.close();
            if (scrollRoot) scrollRoot.style.overflow = previousOverflow;
            if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus({preventScroll: true});
        };
    }, []);

    const dismissBackdrop = (event) => {
        if (event.target !== event.currentTarget) return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onDismiss();
    };

    const scrollToEvidence = (index) => {
        const target = bodyRef.current.querySelector(`[data-evidence-point="${index}"]`);
        if (!target) return;
        const body = bodyRef.current;
        const navigationHeight = body.querySelector('nav')?.getBoundingClientRect().height || 0;
        const top = target.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - navigationHeight - 24;
        target.focus({preventScroll: true});
        body.scrollTo({top, behavior: motionPaused || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    };

    const scrollToSection = (id) => {
        const target = bodyRef.current.querySelector(`#${id}`);
        if (!target) return;
        const body = bodyRef.current;
        body.scrollTo({top: target.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - 72, behavior: motionPaused || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
        target.focus({preventScroll: true});
    };

    const moveLight = (event) => {
        if (motionPaused || event.pointerType !== 'mouse' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const {clientX, clientY} = event;
        cancelAnimationFrame(pointerFrame.current);
        pointerFrame.current = requestAnimationFrame(() => {
            const dialog = dialogRef.current;
            if (!dialog) return;
            const rect = dialog.getBoundingClientRect();
            dialog.style.setProperty('--dialog-x', `${(clientX - rect.left) / rect.width * 100}%`);
            dialog.style.setProperty('--scene-tilt', `${((clientX - rect.left) / rect.width - 0.5) * 9}deg`);
            dialog.style.setProperty('--scene-rise', `${((clientY - rect.top) / rect.height - 0.5) * -10}px`);
        });
    };

    const keepFocusInDialog = (event) => {
        if (event.key !== 'Tab') return;
        const targets = Array.from(dialogRef.current.querySelectorAll('button, a[href], [tabindex="0"]'));
        const first = targets[0];
        const last = targets[targets.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !targets.includes(active))) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    };

    return <dialog
        ref={dialogRef}
        className={s.summaryDialog}
        data-project={Boolean(scene)}
        data-motion={motionPaused ? 'paused' : 'playing'}
        aria-labelledby='summary-dialog-title'
        onCancel={(event) => { event.preventDefault(); onDismiss(); }}
        onClick={dismissBackdrop}
        onKeyDown={keepFocusInDialog}
        onPointerMove={moveLight}
    >
        <header className={s.summaryDialogHeader}>
            {scene ? <ProjectScene /> : null}
            <div className={s.summaryDialogIdentity}>
                <p className={s.summaryDialogCategory}>{isProject ? labels.project : card.category || labels.domain}{scene ? <span>{scene.code}</span> : null}</p>
                <h2 id='summary-dialog-title' ref={titleRef} tabIndex={-1}>{card.title}</h2>
                {card.period ? <p className={s.summaryDialogPeriod}>{card.period}</p> : null}
                {card.stage ? <p className={s.projectStage}><span>{card.stage}</span></p> : null}
                {scene ? <p className={s.projectFlow}>{scene.flow.map((part, index) => <span key={part}>{index > 0 ? <i aria-hidden='true'>→</i> : null}{part}</span>)}</p> : null}
            </div>
            <div className={s.summaryDialogActions}>
                {scene ? <button type='button' className={s.summaryMotionControl} aria-pressed={motionPaused} onClick={() => setMotionPaused(!motionPaused)} aria-label={t(lang, motionPaused ? '播放动效' : '暂停动效')}>{motionPaused ? '▷' : 'Ⅱ'}</button> : null}
                <button type='button' className={s.summaryDialogClose} onClick={onDismiss} aria-label={labels.close}>×</button>
            </div>
        </header>
        <div ref={bodyRef} className={s.summaryDialogBody} tabIndex={0} role='region' aria-label={isProject ? labels.details : labels.domain}>
            {isProject && card.id && (card.metric || card.architectureKey || card.points?.length) ? <nav className={s.projectDetailNav} aria-label={t(lang, '项目详情章节')}>
                {card.metric ? <button type='button' onClick={() => scrollToSection('project-evidence')}>{t(lang, '成果重点')}</button> : null}
                {card.architectureKey ? <button type='button' onClick={() => scrollToSection('project-architecture')}>{t(lang, '系统架构')} <span aria-hidden='true'>↗</span></button> : null}
                {card.points?.length ? <button type='button' onClick={() => scrollToSection('project-implementation')}>{t(lang, '实现与验证')}</button> : null}
            </nav> : null}
            {card.metric ? <ProjectEvidence lang={lang} card={card} heading={labels.result} sources={card.evidence?.sources || scene?.sources} onInspect={scrollToEvidence} /> : null}
            {isProject && card.architectureKey ? <ProjectArchitecture lang={lang} card={card}/> : null}
            {card.keywords ? <p className={s.summaryDialogKeywords}>{card.keywords}</p> : null}
            {card.desc ? <section>
                <h3>{labels.overview}</h3>
                <p>{emphasizeEvidence(card.desc, emphasis)}</p>
            </section> : null}
            {card.points?.length ? <section id='project-implementation' tabIndex={-1}>
                {isProject ? <h3>{labels.details}</h3> : null}
                <ul className={isProject ? s.summaryDetailPoints : undefined}>{card.points.map((point, index) => {
                    const colon = point.search(/[:：]/);
                    const lead = isProject && colon > 0 && colon < (lang === 'en' ? 48 : 24) ? point.slice(0, colon + 1) : '';
                    return <li key={point} data-evidence-point={index} tabIndex={-1}>
                        {isProject ? <span className={s.summaryPointIndex} aria-hidden='true'>{String(index + 1).padStart(2, '0')}</span> : null}
                        <p>{lead ? <span className={s.summaryPointLead}>{lead}</span> : null}{emphasizeEvidence(point.slice(lead.length), emphasis)}</p>
                    </li>;
                })}</ul>
            </section> : null}
            {card.labels?.length || card.github || card.resources?.length ? <footer className={s.summaryDialogFooter}>
                {card.labels?.length ? <div>{card.labels.map((label) => <span key={label}>{label}</span>)}</div> : null}
                {card.resources?.map((resource) => <a key={resource.href} href={resource.href} target='_blank' rel='noreferrer'>{resource.label} ↗</a>)}
                {card.github ? <a href={card.github} target='_blank' rel='noreferrer'>GitHub ↗</a> : null}
            </footer> : null}
        </div>
    </dialog>;
};

export default SummaryDetailDialog;
